import React, { useState, useEffect, useRef, useCallback } from 'react';
import Modal from './Modal';
import ChatProviderSelector from '../selectors/ChatProviderSelector';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useToast } from '../../hooks/useToast';
import {
  ChatProvider,
  DEFAULT_CHAT_PROVIDER,
  DEFAULT_CHAT_MODEL,
} from '../../api/structs/providers';
import { useAiEditorSettings } from '../../hooks/useAiEditorSettings';
import { AI_EDITOR_PROVIDERS } from '../../api/clipEditorSettings';
import {
  CAPTION_MODEL_OPTIONS,
  CAPTION_MODEL_STORAGE_KEY,
  DEFAULT_CAPTION_MODEL,
} from '../datasets/datasetEditApi';
import { SystemPromptsAPI } from '../../api/systemPrompts';
import ReviewerSettingsSection from './ReviewerSettingsSection';
import MediaSettingsSection from './MediaSettingsSection';
import GenerationSettingsSection from './GenerationSettingsSection';
import ModelsAPI from '../../api/models';
import { AIModel } from '../../api/structs/model';
import {
  VIDEO_ANALYSIS_MODEL_STORAGE_KEY,
  DEFAULT_VIDEO_ANALYSIS_MODEL,
} from '../builder/videoAnalysisSettings';
import { ThemeMode, RoundedMode } from '../../theme';
import { MAPPING_COLORS, buildHighlightedHtml, editorStyle } from '../shared/promptHighlight';

// --- inline prompt editor (same technique as PromptTemplateEditor) ---

interface SystemPromptEditorProps {
  promptId: string;
  label?: string;
}

const SystemPromptEditor: React.FC<SystemPromptEditorProps> = ({ promptId, label }) => {
  const toast = useToast();
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsLoading(true);
    SystemPromptsAPI.get(promptId)
      .then(setContent)
      .catch(() => toast({ text: 'Failed to load prompt', level: 'error' }))
      .finally(() => setIsLoading(false));
  }, [promptId, toast]);

  const syncScroll = useCallback(() => {
    if (textareaRef.current && highlightRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  }, []);

  // Exit fullscreen on Escape without closing the modal
  useEffect(() => {
    if (!isFullscreen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        e.preventDefault();
        setIsFullscreen(false);
      }
    };
    document.addEventListener('keydown', handler, { capture: true });
    return () => document.removeEventListener('keydown', handler, { capture: true });
  }, [isFullscreen]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await SystemPromptsAPI.update(promptId, content);
      toast({ text: 'Saved', level: 'success' });
    } catch {
      toast({ text: 'Failed to save', level: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const highlightedHtml = buildHighlightedHtml(content);
  const placeholders = (content.match(/\{\{(\w+)\}\}/g) || []).filter((v, i, a) => a.indexOf(v) === i);

  if (isLoading) {
    return <div className="flex-1 flex items-center justify-center text-[var(--app-text-muted)] text-sm">Loading…</div>;
  }

  // --- Fullscreen overlay ---
  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-[250] bg-[var(--surface-1)] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-strong)] bg-[var(--surface-2)] flex-shrink-0">
          <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-[var(--app-text)]">
            {label || promptId}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsFullscreen(false)}
              className="btn btn-sm btn-ghost font-mono text-[var(--app-text-muted)] hover:text-[var(--app-text)]"
            >
              Exit Fullscreen (Esc)
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 relative bg-[var(--surface-1)]">
          <div
            ref={highlightRef}
            className="absolute inset-0 overflow-auto pointer-events-none text-[var(--app-text)]"
            style={editorStyle}
            dangerouslySetInnerHTML={{ __html: highlightedHtml + '\n' }}
          />
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onScroll={syncScroll}
            className="absolute inset-0 w-full h-full bg-transparent outline-none resize-none"
            style={{ ...editorStyle, color: 'transparent', caretColor: 'var(--app-text)' }}
            spellCheck={false}
          />
        </div>

        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-[var(--border-strong)] bg-[var(--surface-2)] flex-shrink-0">
          <div className="flex-1 min-w-0">
            {placeholders.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                {placeholders.map((ph, i) => {
                  const [fg, bg] = MAPPING_COLORS[i % MAPPING_COLORS.length];
                  return <code key={ph} className="px-1.5 py-0.5 text-[10px] font-bold tracking-widest uppercase border border-[var(--border-subtle)]" style={{ color: fg, background: bg }}>{ph}</code>;
                })}
              </div>
            )}
          </div>
          <button onClick={handleSave} disabled={isSaving} className="btn btn-primary btn-sm">
            {isSaving ? 'Saving...' : 'Save System Prompt'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 flex-1 min-h-0">
      <div className="relative border border-[var(--border-strong)] bg-[var(--surface-1)] overflow-hidden flex-1 min-h-0 shadow-[4px_4px_0_0_var(--border-strong)]">
        <button
          type="button"
          onClick={() => setIsFullscreen(true)}
          title="Fullscreen (F11)"
          className="absolute top-1.5 right-1.5 z-10 btn btn-sm btn-ghost font-mono text-[var(--app-text-muted)] hover:text-[var(--app-text)] px-1.5 py-0.5 text-xs bg-[var(--surface-1)]"
        >
          ⛶
        </button>

        <div className="relative h-full w-full">
          <div
            ref={highlightRef}
            className="absolute inset-0 overflow-hidden pointer-events-none text-[var(--app-text)]"
            style={editorStyle}
            dangerouslySetInnerHTML={{ __html: highlightedHtml + '\n' }}
          />
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onScroll={syncScroll}
            className="absolute inset-0 w-full h-full bg-transparent outline-none resize-none"
            style={{ ...editorStyle, color: 'transparent', caretColor: 'var(--app-text)' }}
            spellCheck={false}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2 flex-shrink-0">
        {placeholders.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {placeholders.map((ph, i) => {
              const [fg, bg] = MAPPING_COLORS[i % MAPPING_COLORS.length];
              return (
                <code key={ph} className="px-1.5 py-0.5 text-[10px] font-bold tracking-widest uppercase border border-[var(--border-subtle)]" style={{ color: fg, background: bg }}>
                  {ph}
                </code>
              );
            })}
          </div>
        )}
      </div>
      <div className="flex justify-end flex-shrink-0">
        <button onClick={handleSave} disabled={isSaving} className="btn btn-primary btn-sm">
          {isSaving ? 'Saving...' : 'Save Prompt'}
        </button>
      </div>
    </div>
  );
};

// --- Settings items ---

interface SettingsItem {
  id: string;
  label: string;
}

const SETTINGS_ITEMS: SettingsItem[] = [
  { id: 'appearance', label: 'Appearance' },
  { id: 'prompt-enhancer', label: 'Prompt Enhancer Model' },
  { id: 'ai-editor', label: 'AI Editor Model' },
  { id: 'auto-caption', label: 'Auto-captioning Model' },
  { id: 'video-analysis', label: 'Video Analysis Model' },
  { id: 'reviewer', label: 'Reviewer' },
  { id: 'generation', label: 'Generation' },
];

// --- Main modal ---

interface Props {
  isOpen: boolean;
  onClose: () => void;
  themeMode: ThemeMode;
  roundedMode: RoundedMode;
  onThemeToggle: () => void;
  onRoundedToggle: () => void;
}

const AppSettingsModal: React.FC<Props> = ({ 
  isOpen, 
  onClose,
  themeMode,
  roundedMode,
  onThemeToggle,
  onRoundedToggle
}) => {
  const [activeItem, setActiveItem] = useState('appearance');
  const [promptEnhancerProvider, setPromptEnhancerProvider] = useLocalStorage<ChatProvider>('promptEnhancerProvider', DEFAULT_CHAT_PROVIDER);
  const [promptEnhancerModel, setPromptEnhancerModel] = useLocalStorage('promptEnhancerModel', DEFAULT_CHAT_MODEL);
  const [captionModel, setCaptionModel] = useLocalStorage<string>(CAPTION_MODEL_STORAGE_KEY, DEFAULT_CAPTION_MODEL);
  const [videoAnalysisModel, setVideoAnalysisModel] = useLocalStorage<string>(VIDEO_ANALYSIS_MODEL_STORAGE_KEY, DEFAULT_VIDEO_ANALYSIS_MODEL);
  const [videoModels, setVideoModels] = useState<AIModel[]>([]);
  const [videoModelsError, setVideoModelsError] = useState(false);
  const {
    provider: aiEditorProvider,
    model: aiEditorModel,
    setProvider: setAiEditorProvider,
    setModel: setAiEditorModel,
  } = useAiEditorSettings();

  // Load the video-capable model list lazily the first time the panel is opened.
  useEffect(() => {
    if (!isOpen || activeItem !== 'video-analysis' || videoModels.length > 0) return;
    let cancelled = false;
    ModelsAPI.getVideoInputModels()
      .then((models) => {
        if (!cancelled) {
          setVideoModels(models);
          setVideoModelsError(false);
        }
      })
      .catch(() => {
        if (!cancelled) setVideoModelsError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, activeItem, videoModels.length]);

  // Ensure the currently-selected model is always an option, even if the live
  // list omits it (registry hiccup, or a model the user set previously).
  const videoModelOptions = React.useMemo(() => {
    const opts = videoModels.map((m) => ({ id: m.id, label: m.name || m.id }));
    if (videoAnalysisModel && !opts.some((o) => o.id === videoAnalysisModel)) {
      opts.unshift({ id: videoAnalysisModel, label: videoAnalysisModel });
    }
    return opts;
  }, [videoModels, videoAnalysisModel]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Settings" size="xl" overflow="visible">
      <div className="flex flex-col lg:flex-row gap-0" style={{ minHeight: 460 }}>
        {/* Sidebar Nav */}
        <div className="w-full lg:w-52 flex-shrink-0 border-b lg:border-b-0 lg:border-r border-[var(--border-strong)] pb-4 lg:pb-0 lg:pr-4 lg:mr-5 mb-4 lg:mb-0">
          <nav className="flex flex-row lg:flex-col gap-1 overflow-x-auto lg:overflow-visible no-scrollbar">
            {SETTINGS_ITEMS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveItem(tab.id)}
                className={`text-left px-3 py-2 font-bold uppercase tracking-wider text-xs leading-tight transition-colors whitespace-nowrap lg:whitespace-normal ${
                  activeItem === tab.id
                    ? 'bg-[var(--surface-3)] text-[var(--app-text)]'
                    : 'text-[var(--app-text-muted)] hover:text-[var(--app-text)] hover:bg-[var(--surface-2)]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content Area — scrolls on its own so the header/tab rail stay put
            and a tall pane (e.g. Reviewer) can never overflow past the modal. */}
        <div className="flex-1 flex flex-col min-h-0 min-w-0 max-h-[70vh] overflow-y-auto">
          {activeItem === 'appearance' && (
            <>
              <div className="mb-4 flex-shrink-0">
                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--app-text)] mb-1">Appearance</h3>
                <p className="text-xs text-[var(--app-text-muted)]">
                  Customize the look and feel of the application.
                </p>
              </div>
              <div className="flex flex-col gap-4 max-w-sm">
                <button
                  type="button"
                  role="switch"
                  aria-checked={themeMode === 'dark'}
                  className="theme-switch w-full justify-between"
                  onClick={onThemeToggle}
                >
                  <span className="theme-switch-label">{themeMode === 'dark' ? 'Dark Mode' : 'Light Mode'}</span>
                  <span className="theme-switch-track">
                    <span className={`theme-switch-thumb ${themeMode === 'dark' ? 'translate-x-5' : 'translate-x-0'}`} />
                  </span>
                </button>

                <button
                  type="button"
                  role="switch"
                  aria-checked={roundedMode === 'rounded'}
                  className="theme-switch w-full justify-between"
                  onClick={onRoundedToggle}
                >
                  <span className="theme-switch-label">{roundedMode === 'rounded' ? 'Rounded UI' : 'Sharp UI'}</span>
                  <span className="theme-switch-track">
                    <span className={`theme-switch-thumb ${roundedMode === 'rounded' ? 'translate-x-5' : 'translate-x-0'}`} />
                  </span>
                </button>
              </div>
            </>
          )}

          {activeItem === 'prompt-enhancer' && (
            <>
              <div className="mb-4 flex-shrink-0">
                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--app-text)] mb-1">Prompt Enhancer</h3>
                <p className="text-xs text-[var(--app-text-muted)]">
                  AI model and system prompt used to expand and enrich video ideas before generation.
                </p>
              </div>
              <div className="mb-4 flex-shrink-0">
                <h4 className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] mb-2">Model</h4>
                {/* Enhancement runs through ai.GenerateWithTelemetry, which
                    gained Vertex AI dispatch (commit f211a52) — full
                    CHAT_PROVIDERS is safe to offer here again. */}
                <ChatProviderSelector
                  provider={promptEnhancerProvider}
                  model={promptEnhancerModel}
                  onProviderChange={setPromptEnhancerProvider}
                  onModelChange={setPromptEnhancerModel}
                />
              </div>
              <p className="text-xs text-[var(--app-text-muted)]">
                System prompt editing now lives in Admin.
              </p>
            </>
          )}

          {activeItem === 'ai-editor' && (
            <>
              <div className="mb-4 flex-shrink-0">
                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--app-text)] mb-1">AI Editor</h3>
                <p className="text-xs text-[var(--app-text-muted)]">
                  Model used by the floating carousel clip chat editor.
                </p>
              </div>
              <div className="mb-4 flex-shrink-0">
                <h4 className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] mb-2">Model</h4>
                <ChatProviderSelector
                  provider={aiEditorProvider}
                  model={aiEditorModel}
                  onProviderChange={setAiEditorProvider}
                  onModelChange={setAiEditorModel}
                  providers={AI_EDITOR_PROVIDERS}
                />
              </div>
              <p className="text-xs text-[var(--app-text-muted)]">
                Saved separately from Prompt Enhancer so clip edits can use a dedicated chat model.
              </p>
            </>
          )}

          {activeItem === 'auto-caption' && (
            <>
              <div className="mb-4 flex-shrink-0">
                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--app-text)] mb-1">Auto-captioning</h3>
                <p className="text-xs text-[var(--app-text-muted)]">
                  Vision model used to auto-write LoRA training captions for dataset images in the curation view.
                </p>
              </div>
              <div className="mb-4 flex-shrink-0 max-w-sm">
                <h4 className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] mb-2">Model</h4>
                <select
                  className="select w-full"
                  value={captionModel}
                  onChange={(e) => setCaptionModel(e.target.value)}
                >
                  {CAPTION_MODEL_OPTIONS.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-[var(--app-text-muted)]">
                Each caption is an external AI call and is billed per image. If a model isn't priced in the registry, captioning fails closed and asks you to pick another model.
              </p>
            </>
          )}

          {activeItem === 'video-analysis' && (
            <>
              <div className="mb-4 flex-shrink-0">
                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--app-text)] mb-1">Video Analysis</h3>
                <p className="text-xs text-[var(--app-text-muted)]">
                  Model used by the Builder to analyse an uploaded video into a scene-by-scene description.
                </p>
              </div>
              <div className="mb-4 flex-shrink-0 max-w-sm">
                <h4 className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] mb-2">Model</h4>
                <select
                  className="select w-full"
                  value={videoAnalysisModel}
                  onChange={(e) => setVideoAnalysisModel(e.target.value)}
                >
                  {videoModelOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {videoModelsError && (
                  <p className="text-xs text-[var(--app-text-muted)] mt-2">
                    Couldn't load the live model list — showing your saved choice. Try reopening Settings.
                  </p>
                )}
              </div>
              <p className="text-xs text-[var(--app-text-muted)]">
                Only models that accept video input are listed, across every chat provider. Each analysis is an external, credit-billed call, and the server re-checks video capability before running.
              </p>
            </>
          )}

          {/* Mounted with the tab so it loads on open (same shape as the
              system-prompt editor); their state lives in the sections
              themselves. Describing uploads sits with the reviewer because it
              is what gives the judge — and the generators — something to read
              about an uploaded image, though it is saved on its own. */}
          {activeItem === 'reviewer' && (
            <>
              <ReviewerSettingsSection />
              <MediaSettingsSection />
            </>
          )}

          {/* Mounted with the tab, like the reviewer: it reads the account's locked
              generation settings on open and keeps its own state. */}
          {activeItem === 'generation' && <GenerationSettingsSection />}

          {activeItem === 'builder-prompt' && (
            <>
              <div className="mb-4 flex-shrink-0">
                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--app-text)] mb-1">Builder Prompt</h3>
                <p className="text-xs text-[var(--app-text-muted)]">
                  The system instructions that guide the chat AI in the Builder view.
                </p>
              </div>
              <div className="flex-1 flex flex-col min-h-0">
                <SystemPromptEditor promptId="builder-system-prompt" label="Builder Prompt" />
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AppSettingsModal;
